import {
    ActivityPointsMultiplierTierDto,
    ActivityType,
    WorkoutCategory
} from '../../core/api/FitnessActivityClient';

export type TierCountUnit = 'steps' | 'minutes' | 'meters' | 'counts';

export interface TierTimelineSegment {
    key: string;
    widthPercent: number;
    isOpenEnded: boolean;
    rangeLabel: string;
    multiplierLabel: string;
    multiplier: number;
}

const DISTANCE_WORKOUT_CATEGORIES = new Set<WorkoutCategory>([
    WorkoutCategory.FOOT,
    WorkoutCategory.WHEELS,
    WorkoutCategory.WATER,
    WorkoutCategory.WINTER
]);

const TIME_WORKOUT_CATEGORIES = new Set<WorkoutCategory>([
    WorkoutCategory.TEAM_SPORTS,
    WorkoutCategory.STATIONARY_TRAININGS,
    WorkoutCategory.OTHER
]);

export function resolveTierCountUnit(
    activityType?: ActivityType | null,
    workoutCategory?: WorkoutCategory | null
): TierCountUnit {
    if (!activityType) {
        return 'counts';
    }

    if (activityType === ActivityType.DAILY_STEPS) {
        return 'steps';
    }

    if (activityType === ActivityType.FACILITY_VISIT) {
        return 'minutes';
    }

    if (activityType === ActivityType.WORKOUT) {
        if (!workoutCategory) {
            return 'meters';
        }

        if (DISTANCE_WORKOUT_CATEGORIES.has(workoutCategory)) {
            return 'meters';
        }

        if (TIME_WORKOUT_CATEGORIES.has(workoutCategory)) {
            return 'minutes';
        }
    }

    return 'counts';
}

export function formatTierUnitLabel(unit: TierCountUnit): string {
    return `Counts by: ${unit}`;
}

export function formatTierRangeCompact(tier: ActivityPointsMultiplierTierDto): string {
    const min = tier.min.toLocaleString();
    const max = tier.max !== undefined && tier.max !== null
        ? tier.max.toLocaleString()
        : '∞';

    return `${min}-${max}`;
}

export function formatTierMultiplier(multiplier: number): string {
    return `${multiplier.toFixed(2).replace(/\.?0+$/, '')}x`;
}

function toTierKey(tier: ActivityPointsMultiplierTierDto, index: number): string {
    return `${tier.min}-${tier.max ?? 'inf'}-${index}`;
}

function toFiniteSpan(tier: ActivityPointsMultiplierTierDto): number {
    if (tier.max === undefined || tier.max === null) {
        return 1;
    }

    return Math.max(1, tier.max - tier.min);
}

export function buildTierTimelineSegments(
    tiers: ActivityPointsMultiplierTierDto[]
): TierTimelineSegment[] {
    if (tiers.length === 0) {
        return [];
    }

    const sortedTiers = [...tiers].sort((a, b) => a.min - b.min);
    const lastTier = sortedTiers[sortedTiers.length - 1];
    const hasOpenEndedTail = lastTier.max === undefined || lastTier.max === null;

    const finiteTiers = hasOpenEndedTail
        ? sortedTiers.slice(0, -1)
        : sortedTiers;

    const finiteShares = new Map<string, number>();
    const finiteTimelineShare = hasOpenEndedTail ? 85 : 100;
    const openEndedShare = hasOpenEndedTail
        ? finiteTiers.length > 0
            ? 15
            : 100
        : 0;

    if (finiteTiers.length > 0) {
        const finiteSpans = finiteTiers.map(toFiniteSpan);
        const totalSpan = finiteSpans.reduce((sum, span) => sum + span, 0);

        finiteTiers.forEach((tier, index) => {
            const widthPercent = totalSpan > 0
                ? (finiteSpans[index] / totalSpan) * finiteTimelineShare
                : finiteTimelineShare / finiteTiers.length;

            finiteShares.set(toTierKey(tier, index), widthPercent);
        });
    }

    return sortedTiers.map((tier, index) => {
        const isOpenEnded = hasOpenEndedTail && index === sortedTiers.length - 1;
        const key = toTierKey(tier, index);

        return {
            key,
            widthPercent: isOpenEnded
                ? openEndedShare
                : finiteShares.get(key) ?? 0,
            isOpenEnded,
            rangeLabel: formatTierRangeCompact(tier),
            multiplierLabel: formatTierMultiplier(tier.multiplier),
            multiplier: tier.multiplier
        };
    });
}
